import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Hub } from '@aws-amplify/core'
import type { User } from '@/types/auth'
import * as authService from '@/services/auth'
import { TenantNotActivatedError } from '@/services/auth'
import { ensureAuthenticatedUserInDatabase, createAuthenticatedUser, ApiRequestError } from '@/services/api'
import { queryClient } from '@/app/queryClient'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  authError: string | null
  signIn: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [authError, setAuthError] = useState<string | null>(null)
  const hasInitialized = useRef(false)
  const authCheckInFlight = useRef<Promise<void> | null>(null)
  const dbUserSyncAttemptedForUser = useRef<string | null>(null)

  const checkAuthStatus = useCallback(async () => {
    // Prevent overlapping checks from mount + Hub events (reduces UI flicker/loops).
    if (authCheckInFlight.current) {
      return authCheckInFlight.current
    }

    const run = (async () => {
      setIsLoading(true)
      try {
        const currentUser = await authService.getUser()
        setUser(currentUser)
        if (currentUser) {
          await ensureDbUser(currentUser.id)
        }
      } catch (error) {
        console.error('Error checking auth status:', error)
        setUser(null)
      } finally {
        setIsLoading(false)
        authCheckInFlight.current = null
      }
    })()

    authCheckInFlight.current = run
    return run
  }, [])

  // On mount: handle a possible MSAL redirect, then check session.
  // In React StrictMode (dev), effects run twice, so guard to initialize once.
  useEffect(() => {
    if (hasInitialized.current) return
    hasInitialized.current = true

    ;(async () => {
      try {
        const wasRedirect = await authService.handleMicrosoftCallback()
        if (wasRedirect) {
          console.debug('[auth] MSAL redirect handled, checking session')
        }
      } catch (err) {
        console.error('[auth] MSAL callback error', err)
        if (err instanceof TenantNotActivatedError) {
          setAuthError(err.message)
        } else {
          setAuthError('Sign-in failed. Please try again.')
        }
      }
      await checkAuthStatus()
    })()
  }, [checkAuthStatus])

  // Listen to Cognito auth events (custom-auth still emits these)
  useEffect(() => {
    const hubListener = Hub.listen('auth', ({ payload }) => {
      try {
        console.debug('[auth][hub]', payload?.event, payload)
      } catch {
        // ignore
      }
      switch (payload.event) {
        case 'signedIn':
          checkAuthStatus()
          break
        case 'signedOut':
          setUser(null)
          dbUserSyncAttemptedForUser.current = null
          break
        case 'tokenRefresh':
          // Avoid full auth status + DB sync on every refresh event.
          // This can cause retry storms when backend endpoints are failing.
          break
      }
    })

    return () => hubListener()
  }, [checkAuthStatus])

  async function ensureDbUser(currentUserId: string) {
    if (dbUserSyncAttemptedForUser.current === currentUserId) {
      return
    }
    dbUserSyncAttemptedForUser.current = currentUserId

    try {
      await ensureAuthenticatedUserInDatabase()
    } catch (fetchError) {
      if (fetchError instanceof ApiRequestError && fetchError.status === 404) {
        try {
          await createAuthenticatedUser()
          console.debug('[auth] DB user created')
        } catch (createError) {
          console.error('[auth] Failed to create DB user', createError)
          // Creation failed — sign the user out so they don't get into the app
          // with a broken account state.
          dbUserSyncAttemptedForUser.current = null
          setUser(null)
          const message =
            createError instanceof ApiRequestError && createError.status === 404
              ? 'Your account is not linked to an active organization. Contact your administrator.'
              : 'Account setup failed. Please try signing in again or contact support.'
          setAuthError(message)
        }
      } else {
        console.error('[auth] Failed to fetch DB user', fetchError)
        // Network or server errors are transient — don't block the user, they can retry.
      }
    }
  }

  async function signIn() {
    try {
      setAuthError(null)
      await authService.signInWithMicrosoft()
    } catch (error) {
      console.error('Sign in error:', error)
      throw error
    }
  }

  async function signOut() {
    try {
      await authService.signOut()
      queryClient.clear()
      setUser(null)
    } catch (error) {
      console.error('Sign out error:', error)
      throw error
    }
  }

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    authError,
    signIn,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
